import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AgentResponseSegment } from '../ui-components';
import { ComponentRenderer } from './ComponentRenderer';
import { ComponentErrorBoundary } from './ComponentErrorBoundary';

export type MessageRole = 'user' | 'agent';

interface MessageProps {
  role: MessageRole;
  content: string;
  segments?: AgentResponseSegment[];
}

function TextBubble({ role, text }: { role: MessageRole; text: string }): React.JSX.Element {
  return (
    <div className="widget-message-bubble">
      {role === 'agent' ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            table: ({ children }) => (
              <div className="widget-message-table-wrap">
                <table>{children}</table>
              </div>
            ),
          }}
        >
          {text}
        </ReactMarkdown>
      ) : (
        text
      )}
    </div>
  );
}

export function Message({ role, content, segments }: MessageProps): React.JSX.Element {
  const hasComponents = Boolean(segments?.some((s) => s.type === 'component'));
  return (
    <div className={`widget-message widget-message-${role}`}>
      <div className={`widget-message-column${hasComponents ? ' widget-message-column-wide' : ''}`}>
        {segments
          ? segments.map((segment, i) =>
              segment.type === 'text' ? (
                <TextBubble key={i} role={role} text={segment.value} />
              ) : (
                <ComponentErrorBoundary key={i}>
                  <ComponentRenderer component={segment.value} />
                </ComponentErrorBoundary>
              ),
            )
          : content && <TextBubble role={role} text={content} />}
      </div>
    </div>
  );
}

export function TypingIndicator(): React.JSX.Element {
  return (
    <div className="widget-message widget-message-agent">
      <div className="widget-message-bubble widget-typing">
        <span className="widget-typing-dot" />
        <span className="widget-typing-dot" />
        <span className="widget-typing-dot" />
      </div>
    </div>
  );
}
